import {
  parseRailwayJson,
  railwayOutputTail,
  runRailway
} from '@next-adapter/init/railway/runner.js'
import type {
  RailwayBucketCredentials,
  RailwayBucketProvisionResult,
  RailwayService,
  RailwaySession
} from '@next-adapter/init/railway/types.js'

interface RailwayBucketSummary {
  id: string
  name: string
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function parseService(value: unknown): RailwayService | undefined {
  if (!value || typeof value !== 'object') return undefined
  const service = value as Record<string, unknown>
  if (!isNonEmptyString(service.id) || !isNonEmptyString(service.name)) return undefined
  const replicas =
    service.replicas && typeof service.replicas === 'object'
      ? (service.replicas as Record<string, unknown>)
      : undefined
  return {
    id: service.id,
    name: service.name,
    replicaCount: typeof replicas?.configured === 'number' ? replicas.configured : undefined
  }
}

export async function listRailwayServices(
  session: RailwaySession,
  cwd: string
): Promise<RailwayService[]> {
  const result = await runRailway(
    session.runner,
    ['service', 'list', '--environment', session.project.environmentName, '--json'],
    {
      cwd,
      mode: 'capture',
      env: session.env
    }
  )
  if (!result.success) {
    throw new Error(
      railwayOutputTail(`${result.stdout}\n${result.stderr}`) ?? 'Could not list Railway services.'
    )
  }

  const payload = parseRailwayJson<unknown>(result.stdout)
  if (!Array.isArray(payload)) {
    throw new Error('Railway returned an invalid service list response.')
  }

  const services = payload.map(parseService)
  if (services.some((service) => !service)) {
    throw new Error('Railway returned an invalid service list response.')
  }

  return services as RailwayService[]
}

export async function ensureRailwayAppService(
  session: RailwaySession,
  cwd: string,
  requestedName = 'web'
): Promise<RailwayService> {
  const services = await listRailwayServices(session, cwd)
  const existing = services.find(
    (service) =>
      service.id.toLowerCase() === requestedName.toLowerCase() ||
      service.name.toLowerCase() === requestedName.toLowerCase()
  )
  if (existing) return existing

  const result = await runRailway(session.runner, ['add', '--service', requestedName, '--json'], {
    cwd,
    mode: 'capture',
    env: session.env
  })
  const created = result.success
    ? parseService(parseRailwayJson<unknown>(result.stdout))
    : undefined
  if (!created) {
    throw new Error(
      railwayOutputTail(`${result.stdout}\n${result.stderr}`) ??
        `Could not create Railway service ${requestedName}.`
    )
  }
  return created
}

export async function readRailwayServiceVariables(
  session: RailwaySession,
  cwd: string,
  service: string
): Promise<Record<string, string>> {
  const result = await runRailway(
    session.runner,
    [
      'variable',
      'list',
      '--service',
      service,
      '--environment',
      session.project.environmentName,
      '--json'
    ],
    { cwd, mode: 'capture', env: session.env }
  )
  if (!result.success) {
    throw new Error(
      railwayOutputTail(`${result.stdout}\n${result.stderr}`) ??
        `Could not read Railway variables for ${service}.`
    )
  }

  const payload = parseRailwayJson<unknown>(result.stdout)
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error(`Railway returned an invalid variable list response for ${service}.`)
  }

  const entries = Object.entries(payload)
  if (entries.some(([, value]) => typeof value !== 'string')) {
    throw new Error(`Railway returned an invalid variable list response for ${service}.`)
  }

  return Object.fromEntries(entries) as Record<string, string>
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds))
}

async function waitForPostgresVariables(
  session: RailwaySession,
  cwd: string,
  service: RailwayService
): Promise<Record<string, string>> {
  for (let attempt = 0; attempt < 20; attempt++) {
    const variables = await readRailwayServiceVariables(session, cwd, service.name)
    if (variables.DATABASE_PUBLIC_URL && variables.DATABASE_URL) return variables
    if (attempt < 19) await delay(1_500)
  }
  return {}
}

export async function ensureRailwayPostgres(
  session: RailwaySession,
  cwd: string
): Promise<{ service: RailwayService; databaseUrl: string }> {
  const services = await listRailwayServices(session, cwd)
  const candidates = services.filter((service) => /^postgres(?:[-_ ].*)?$/i.test(service.name))

  for (const candidate of candidates) {
    const variables = await readRailwayServiceVariables(session, cwd, candidate.name)
    if (variables.DATABASE_PUBLIC_URL) {
      return { service: candidate, databaseUrl: variables.DATABASE_PUBLIC_URL }
    }
  }
  if (candidates.length > 0) {
    throw new Error(
      `Railway Postgres service ${candidates.map((candidate) => candidate.name).join(', ')} exists, but DATABASE_PUBLIC_URL is not available. Rerun init after the database is ready.`
    )
  }

  const result = await runRailway(session.runner, ['add', '--database', 'postgres', '--json'], {
    cwd,
    mode: 'capture',
    env: session.env
  })
  const payload = result.success
    ? parseRailwayJson<Record<string, unknown>>(result.stdout)
    : undefined
  const serviceName = payload && isNonEmptyString(payload.serviceName) ? payload.serviceName : null
  const serviceId = payload && isNonEmptyString(payload.serviceId) ? payload.serviceId : null
  if (!result.success || !serviceName) {
    throw new Error(
      railwayOutputTail(`${result.stdout}\n${result.stderr}`) ??
        'Could not provision Railway Postgres.'
    )
  }

  const service = { id: serviceId ?? serviceName, name: serviceName }
  const variables = await waitForPostgresVariables(session, cwd, service)
  if (!variables.DATABASE_PUBLIC_URL) {
    throw new Error(
      `Railway created ${service.name}, but DATABASE_PUBLIC_URL was not available. Rerun init after the database is ready.`
    )
  }

  return { service, databaseUrl: variables.DATABASE_PUBLIC_URL }
}

function parseBucket(value: unknown): RailwayBucketSummary | undefined {
  if (!value || typeof value !== 'object') return undefined
  const bucket = value as Record<string, unknown>
  return isNonEmptyString(bucket.id) && isNonEmptyString(bucket.name)
    ? { id: bucket.id, name: bucket.name }
    : undefined
}

function parseBucketCredentials(value: unknown): RailwayBucketCredentials | undefined {
  if (!value || typeof value !== 'object') return undefined
  const credentials = value as Record<string, unknown>
  const keys = [
    'endpoint',
    'accessKeyId',
    'secretAccessKey',
    'bucketName',
    'region',
    'urlStyle'
  ] as const
  if (!keys.every((key) => isNonEmptyString(credentials[key]))) return undefined

  return {
    endpoint: credentials.endpoint as string,
    accessKeyId: credentials.accessKeyId as string,
    secretAccessKey: credentials.secretAccessKey as string,
    bucketName: credentials.bucketName as string,
    region: credentials.region as string,
    urlStyle: credentials.urlStyle as string
  }
}

export async function ensureRailwayBucket(
  session: RailwaySession,
  cwd: string,
  options: { name?: string; region: string }
): Promise<RailwayBucketProvisionResult> {
  const name = options.name ?? 'media'
  const environmentArgs = ['--environment', session.project.environmentName]
  const list = await runRailway(session.runner, ['bucket', 'list', ...environmentArgs, '--json'], {
    cwd,
    mode: 'capture',
    env: session.env
  })
  if (!list.success) {
    throw new Error(
      railwayOutputTail(`${list.stdout}\n${list.stderr}`) ?? 'Could not list Railway buckets.'
    )
  }

  const listPayload = parseRailwayJson<unknown>(list.stdout)
  if (!Array.isArray(listPayload)) {
    throw new Error('Railway returned an invalid bucket list response.')
  }

  const parsedBuckets = listPayload.map(parseBucket)
  if (parsedBuckets.some((bucket) => !bucket)) {
    throw new Error('Railway returned an invalid bucket list response.')
  }
  const buckets = parsedBuckets as RailwayBucketSummary[]
  let bucket = buckets.find((candidate) => candidate.name.toLowerCase() === name.toLowerCase())

  if (!bucket) {
    const create = await runRailway(
      session.runner,
      ['bucket', 'create', name, '--region', options.region, ...environmentArgs, '--json'],
      { cwd, mode: 'capture', env: session.env }
    )
    const createPayload = create.success
      ? parseRailwayJson<Record<string, unknown>>(create.stdout)
      : undefined
    bucket = createPayload ? parseBucket(createPayload) : undefined
    if (!create.success || !bucket) {
      throw new Error(
        railwayOutputTail(`${create.stdout}\n${create.stderr}`) ??
          `Could not create Railway bucket ${name}.`
      )
    }
    if (createPayload?.staged === true && createPayload.committed !== true) {
      throw new Error(
        `Railway staged bucket ${name} because the environment has pending changes. Commit those changes in Railway, then rerun init.`
      )
    }
  }

  const credentialsResult = await runRailway(
    session.runner,
    ['bucket', 'credentials', '--bucket', bucket.id, ...environmentArgs, '--json'],
    { cwd, mode: 'capture', env: session.env }
  )
  const credentials = credentialsResult.success
    ? parseBucketCredentials(parseRailwayJson<unknown>(credentialsResult.stdout))
    : undefined
  if (!credentials) {
    throw new Error(
      railwayOutputTail(`${credentialsResult.stdout}\n${credentialsResult.stderr}`) ??
        `Could not retrieve credentials for Railway bucket ${bucket.name}.`
    )
  }

  return { resource: bucket, credentials }
}
